import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { injectIntl, Message } from 'sb-react-intl'
import ValueLink, { Input as ValueLinkInput } from 'sb-valuelink'
import { compose, object } from 'common/helpers'

import { CircleLoader } from 'components/_new/ui'
import VisuallyHidden from 'components/ui/VisuallyHidden/VisuallyHidden'

import TextArea from './TextArea/TextArea'

import s from './Input.scss'

import lockIcon from './images/lock.svg'
import magnifierIcon from './images/magnifier.svg'


const getUniqueId = ((id) => () => String(++id))(1)

class Input extends Component {

  static propTypes = {
    intl: PropTypes.object, // @injectIntl
    className: PropTypes.string,
    rootClassName: PropTypes.string,
    inputClassName: PropTypes.string,
    label: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]),
    placeholder: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]),
    type: PropTypes.string,
    name: PropTypes.string,
    valueLink: PropTypes.object,
    focusOnInit: PropTypes.bool,
    withLockIcon: PropTypes.bool,
    withMagnifierIcon: PropTypes.bool,
    withLoader: PropTypes.bool,
    rightLoader: PropTypes.bool,
    multiline: PropTypes.bool,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    dataTestId: PropTypes.string,
    onError: PropTypes.func,
  }

  static defaultProps = {
    focusOnInit: false,
    multiline: false,
    disabled: false,
    readOnly: false,
    required: false,
    type: 'text',
  }

  constructor({ id }) {
    super()

    this.inputId = id || `input_${getUniqueId()}`

    this.state = {
      value: '',
    }
  }

  componentDidUpdate({ valueLink: { error: prevError } = {} }) {
    const { valueLink: { error: nextError } = {} } = this.props

    if (nextError && (!prevError || (nextError.id !== prevError.id))) {
      this.handleError(nextError)
    }
  }

  handleError = () => {
    const { onError, valueLink } = this.props

    if (onError && typeof onError === 'function') {
      onError(valueLink.error, valueLink)
    }
  }

  renderLeftPart() {
    const { withLoader, rightLoader, withMagnifierIcon } = this.props

    const circleLoaderClassName = cx(s.circleLoader, {
      [s.right]: rightLoader,
    })

    if (withLoader) {
      return (
        <CircleLoader className={circleLoaderClassName} />
      )
    }

    if (withMagnifierIcon) {
      return (
        <img className={s.magnifierIcon} src={magnifierIcon} alt="magnifier icon" />
      )
    }

    return null
  }

  renderCommonParts() {
    const {
      intl, inputClassName, label, placeholder, type, name,
      focusOnInit, multiline, disabled, readOnly, required,
      dataTestId, autoComplete,
      ...rest
    } = this.props

    // TODO this ductape for landings...
    const valueLink     = this.props.valueLink || ValueLink.state(this, 'value') // eslint-disable-line

    const value         = valueLink.value // eslint-disable-line
    const valuePresent  = value !== null && value !== undefined && value !== '' && !Number.isNaN(value)

    const inputElementClassName = cx(s.input, inputClassName, {
      [s.labelExist]: label && !placeholder,
      [s.filled]: valuePresent,
    })

    let placeholderValue = placeholder
    if (typeof placeholder === 'object') {
      placeholderValue = intl.formatMessage(placeholder)
    }

    const inputElement = React.createElement(multiline ? TextArea : ValueLinkInput, {
      ...object.removeKeys(rest, [
        'styles',
        'styleSpecial',
        'rootClassName',
        'withMagnifierIcon',
        'withLockIcon',
        'withLoader',
        'rightLoader',
      ]),
      id: this.inputId,
      className: inputElementClassName,
      placeholder: placeholderValue,
      valueLink,
      disabled: disabled || readOnly,
      required,
      autoFocus: !!focusOnInit,
      name,
      type,
      dir: 'auto',
      autoComplete,
      'data-testid': dataTestId,
    })

    let labelElement = (
      <Message
        className={s.label}
        tag="label"
        text={label || placeholder}
        htmlFor={this.inputId}
      />
    )

    if (placeholder) {
      labelElement = <VisuallyHidden>{labelElement}</VisuallyHidden>
    }

    return (
      <Fragment>
        {inputElement}
        {labelElement}
      </Fragment>
    )
  }

  renderLockIcon() {
    const { withLockIcon } = this.props

    return withLockIcon && (
      <img className={s.lockIcon} src={lockIcon} alt="lock icon" />
    )
  }

  render() {
    const {
      valueLink, className, rootClassName, disabled, readOnly, hidden,
      withMagnifierIcon, withLockIcon, withLoader, rightLoader,
    } = this.props

    const error = valueLink && valueLink.error // eslint-disable-line

    const rootElementClassName = cx(s.root, rootClassName, {
      [s.disabled]: disabled || readOnly,
      [s.errored]: error,
      [s.hidden]: hidden,
      [s.withMagnifierIcon]: withMagnifierIcon,
      [s.withLockIcon]: withLockIcon,
      [s.withLoader]: withLoader,
      [s.rightLoader]: rightLoader,
      [s.leftLoader]: !rightLoader,
    })

    const errorNode = (
      <Message
        text={error}
        aria-live="polite"
        role="region"
      />
    )

    return (
      <div className={className}>
        <div className={rootElementClassName}>
          {this.renderLeftPart()}
          {this.renderCommonParts()}
          {this.renderLockIcon()}
        </div>
        {
          Boolean(error) && (
            <div className={s.error}>{errorNode}</div>
          )
        }
      </div>
    )
  }
}

Input.defaultProps = {
  // 'nope' was a hack to remove autoComplete, coz 'off' doesn't work
  // bug: https://bugs.chromium.org/p/chromium/issues/detail?id=914451
  // workarond: https://goo.gl/PeZ856
  // 'nope' is ignored? https://github.com/Rendez/downshift/commit/bf876844925545bab3cb32d6ff66c1beeb056199
  autoComplete: 'off',
}


export default compose(
  injectIntl
)(Input)
